import React, {useEffect, useRef} from 'react';
import {useDebouncedCallback} from 'use-debounce';
import {useMediaQuery} from 'react-responsive';
import FadeIn from 'react-fade-in';
import millify from 'millify';
import lscache from 'lscache';
import ReactGA from 'react-ga4';
import {APICall} from './../../api/api';
import breakpoints from './../helpers/breakpoints';
import indicatorStyles from '../helpers/indicatorsStyles';
import useStore from '../../store/store';
import usePersistentStore from '../../store/persistentStore';

import AnalyticsIcon from './../img/ic_analytics.svg';
import Trend from './Trend';
import DecilesView from './DecilesView';
import Realtime from './Realtime';
import {PeriodPicker} from './PeriodPicker';
import {ComparisonModeSelect} from './ComparisonModeSelect';
import MetricsDisplay from './MetricsDisplay_mobile';
import AccountsDetailsTable from './AccountsDetailsTable';
import AccountsPropertiesSelection from './AccountsPropertiesSelection';
import Placeholder from './../misc/Placeholder';
import {Info, Close} from '@material-ui/icons';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import {alpha} from '@material-ui/core/styles/colorManipulator';
import {makeStyles, useTheme} from '@material-ui/core/styles/index';
import Link from '@material-ui/core/Link/index';
import {Alert, Skeleton} from '@material-ui/lab';
import moment from 'moment';
import {ShareDialog} from './ShareDialog';
import {TimezonePicker} from './TimezonePicker';
import usePublishers from '../../hooks/usePublishers';
import HistoricalDataAlert from './HistoricalDataAlert';
import DataTypeSelector from './DataTypeSelector';
import DataExport from './DataExport';

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1),
  },
  statsWrapper: {
    borderBottom: '1px solid #7979793b',
    cursor: 'pointer',
    paddingTop: theme.spacing(0.5),
  },
  metricWrapper: {
    marginBottom: theme.spacing(0.5),
  },
  metricWrapperAudience: {
    marginBottom: theme.spacing(1.5),
  },
  lowerOpacity: {
    opacity: 0.5,
  },
  containerWrapper: {
    borderRadius: 4,
    padding: `0px 20px 20px`,
    [theme.breakpoints.down('lg')]: {
      border: 'none',
      padding: 0,
    },
  },
  gaErrorTooltip: {
    maxWidth: 440,
    padding: 0,
    fontSize: 13,
    lineHeight: 1.5,
  },
  gaErrorTooltipBody: {
    maxHeight: 200,
    overflowY: 'auto',
    padding: theme.spacing(1.5, 2),
    scrollbarWidth: 'thin',
    scrollbarColor: `${alpha('#fff', 0.28)} ${alpha('#000', 0.25)}`,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha('#fff', 0.22),
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: alpha('#000', 0.2),
      borderRadius: 4,
    },
  },
  gaErrorTooltipRow: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
    '&:last-child': {
      paddingBottom: 0,
      marginBottom: 0,
      borderBottom: 'none',
    },
  },
  gaErrorTooltipDomainRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.75),
  },
  gaErrorTooltipDomainImage: {
    height: 20,
    width: 20,
    flexShrink: 0,
    marginRight: 8,
    backgroundColor: 'white',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80%',
    borderRadius: 4,
  },
  gaErrorTooltipDomain: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'inherit',
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  gaErrorTooltipMessage: {
    color: alpha(theme.palette.common.white, 0.92),
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  ...indicatorStyles(theme),
}));

const AccountDetails = React.memo(props => {
  const isMobile = useMediaQuery({query: breakpoints.mobile});
  const classes = useStyles();
  const theme = useTheme();
  const chartRef = useRef();
  const abortControllerRef = useRef();

  const dataType = useStore(state => state.dataType);
  const [calculatedComparison, setCalculatedComparison] = React.useState(null);
  const calculated = useStore(state => state.calculated);
  const setState = useStore(state => state.setState);
  const setPersistentState = usePersistentStore(state => state.setState);
  const period = useStore(state => state.period);
  const comparisonPeriod = useStore(state => state.comparisonPeriod);
  const selection = useStore(state => state.selection);
  const [publisherStats, setPublisherStats] = React.useState(false);
  const [domainHistory, setDomainHistory] = React.useState(false);
  const [publisherStatsComparison, setPublisherStatsComparison] = React.useState(false);
  const [analyticsErrors, setAnalyticsErrors] = React.useState([]);
  const [analyticsLoaded, setAnalyticsLoaded] = React.useState(false);
  const [analyticsComparisonLoaded, setAnalyticsComparisonLoaded] = React.useState(false);
  const [analyticsReminderHidden, setAnalyticsReminderHidden] = React.useState(false);
  const [graphType, setGraphType] = React.useState(1);
  const comparisonEnabled = usePersistentStore(state => state.comparisonEnabled);
  const [activeMetrics, setActiveMetrics] = React.useState([
    'Rev',
    'All Imp',
    'Paid Imp',
    'eCPM',
    'fillRate',
    'Viewability',
  ]);
  const {publishersHasOwnGam} = usePublishers();

  const analyticsErrorDomainCount = new Set(analyticsErrors.map(e => e.domain)).size;

  const metricGroups = {
    // Show all
    0: [],
    // Monetization
    1: ['Rev', 'All Imp', 'Paid Imp', 'eCPM', 'fillRate'],
    // Performance
    2: ['Viewability', 'refreshRate', 'Rev Refresh', 'rCPM', 'CTR', 'eCPM Refresh'],
    // Analytics
    3: ['sRPM', 'uRPM', 'pvRPM', 'pageviewsPerSession', 'sessionsPerUser'],
    4: [
      'Rev',
      'All Imp',
      'Paid Imp',
      'eCPM',
      'Viewability',
      'Rev Refresh',
      'eCPM Refresh',
      'rCPM',
      'CTR',
      'sRPM',
      'uRPM',
      'pvRPM',
      'refreshRate',
      'fillRate',
    ],
  };

  useEffect(() => {
    // Create a new AbortController for this effect
    abortControllerRef.current = new AbortController();

    // If placement selected see if Audience shouldn't be disabled
    if (selection.value.placement) {
      // Filter out any audience metrics, if none left turn on first metrics category
      let filteredActiveMetrics = activeMetrics.filter(
        activeMetric => !metricGroups[3].includes(activeMetric)
      );

      setActiveMetrics(filteredActiveMetrics.length > 0 ? filteredActiveMetrics : metricGroups[1]);
    }

    setAnalyticsErrors([]);
    setAnalyticsLoaded(false);
    setAnalyticsComparisonLoaded(false);
    setPublisherStats(false);
    setPublisherStatsComparison(false);
    setDomainHistory(false);
    setState('calculated', null);
    setCalculatedComparison(null);

    getPublisherStats(false);

    return () => {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        abortControllerRef.current.abort();
      }
    };
  }, [selection, period, comparisonPeriod, dataType]);

  useEffect(() => {
    if (calculated && calculatedComparison !== false) {
      getPublisherStats(true);
    }
  }, [calculated]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  let getDomainHistory = async () => {
    try {
      let history = await APICall({
        endpoint: 'getDomainHistory',
        params: `${selection.value.domain}`,
        options: {
          signal: abortControllerRef.current?.signal,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${props.login.token}`,
            'Cache-Control': 'max-age=0, no-cache',
            'Content-Type': 'application/json',
          },
        },
      });
      setDomainHistory(history);
    } catch (e) {
      // Don't log abort errors as they're expected when component unmounts
      if (e.name !== 'AbortError' && !e.message.includes('aborted')) {
        console.log('error getting domain history', e);
      }
    }
  };

  const getRouteByDataType = () => {
    switch (dataType) {
      case 'setupad':
        return 'totalclientsetupad';
      case 'combined':
        return 'totalclientown';
      case 'publisher':
        return 'totalclientpublisher';
    }
  };

  const getPublisherStats = async forComparison => {
    try {
      let query = await APICall({
        endpoint: [4, 5, 6, 7].includes(props.login.type) ? getRouteByDataType() : 'totalclient',
        params: `${forComparison ? comparisonPeriod.startDate : period.startDate}/${
          forComparison ? comparisonPeriod.endDate : period.endDate
        }${selection.value.publisher ? `/${selection.value.publisher}` : ``}${
          selection.value.domain ? `/${selection.value.domain}` : ``
        }${selection.value.placement ? `/${selection.value.placement}` : ``}`,
        options: {
          signal: abortControllerRef.current?.signal,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${props.login.reportingToken}`,
            'Cache-Control': 'max-age=0, no-cache',
          },
        },
      });

      // Add calculations for additional metrics
      query.map((day, i) => {
        query[i].refreshRate =
          day['All Imp'] !== '0' && day['All Imp Refresh'] !== '0'
            ? (day['All Imp Refresh'] * 100) / day['All Imp']
            : 0;
        query[i].fillRate =
          day['All Imp'] !== '0' && day['Paid Imp'] !== 0
            ? (day['Paid Imp'] * 100) / day['All Imp']
            : 0;

        // Fill missing eCPM
        query[i].eCPM = day['eCPM'] ? day['eCPM'] : 0;

        // Fill missing rCPM
        query[i].rCPM = day['rCPM'] && day['rCPM'] !== 'NAN' ? day['rCPM'] : 0;
      });

      if (!forComparison) {
        setPublisherStats(query);

        // Ad another empty day if last period date is not equal to last date in data
        if (query.length > 0 && query[query.length - 1].date !== period.endDate) {
          let lastDayData = query[query.length - 1];
          let emptyRows = {};

          Object.keys(lastDayData).map(key => (emptyRows[key] = 0));

          query.push({
            //...lastDayData.map((row, key) => key),
            ...emptyRows,
            date: moment(lastDayData.date).add(1, 'day').format('YYYY-MM-DD'),
          });
        }
      } else {
        setPublisherStatsComparison(query);
      }

      googleAnalyticsCheck(query, forComparison);
    } catch (error) {
      // Don't log abort errors as they're expected when component unmounts
      if (error.name !== 'AbortError' && !error.message.includes('aborted')) {
        //console.log('getPublisherStats error', error.message);
        // // Redirect on response status not being 200
        //console.log('Error', error);
        // history.push('/login');
      }
    }
  };

  let getAnalyticsStatsForProperty = async (dateFrom, dateTo, analyticsProperty) => {
    return await APICall({
      endpoint: 'getAnalyticsDataForDomain',
      options: {
        signal: abortControllerRef.current?.signal,
        method: 'POST',
        body: JSON.stringify({
          analyticsProperty: analyticsProperty,
          dateFrom: dateFrom,
          dateTo: dateTo,
          required: 'both',
        }),
        headers: {
          Authorization: `Bearer ${props.login.token}`,
          'Cache-Control': 'max-age=0, no-cache',
          'Content-Type': 'application/json',
        },
      },
    });
  };

  const domainLabelForAnalyticsProperty = analyticsProperty => {
    if (!analyticsProperty) return 'Unknown domain';
    const domainId = analyticsProperty.domainId;
    const menuChildren = props.login?.menu?.menu?.[1]?.children;
    if (Array.isArray(menuChildren)) {
      const item = menuChildren.find(c => c.path?.[2] == domainId);
      if (item?.data?.menu?.alias) return item.data.menu.alias;
    }
    const p = analyticsProperty.property;
    if (p && typeof p === 'object' && p.label) return p.label;
    return domainId != null ? `Domain ${domainId}` : 'Unknown domain';
  };

  let googleAnalyticsCheck = async (query, forComparison = false) => {
    // If analytics properties are there, return it as array. One if domain level is selected, all if Total for account is selected
    let analyticsProperties =
      props.assignedGoogleProperties !== 'error' &&
      props.assignedGoogleProperties.find(property => property.domainId == selection.value.domain)
        ? [
            props.assignedGoogleProperties.find(
              property => property.domainId == selection.value.domain
            ),
          ]
        : Object.keys(props.assignedGoogleProperties).length > 0 && !selection.value.publisher
          ? props.assignedGoogleProperties
          : null;

    // console.log('analyticsProperties', analyticsProperties, selection)

    // Get Analytics data if needed
    if (query.length > 0 && analyticsProperties) {
      let dateFrom = query[0].date;
      let dateTo = query[query.length - 1].date;

      // Load graph before GA gets loaded on desktop
      if (!isMobile) {
        calculate(query, 0, query.length, forComparison);
      }

      // Load all analytics properties data at once
      let analyticsQueries;
      try {
        analyticsQueries = await Promise.all(
          analyticsProperties.map(async analyticsProperty => {
            return await getAnalyticsStatsForProperty(dateFrom, dateTo, analyticsProperty);
          })
        );
      } catch (error) {
        // Don't log abort errors as they're expected when component unmounts
        if (error.name !== 'AbortError' && !error.message.includes('aborted')) {
          console.log('error getting analytics data', error);
        }
        // If analytics fails, just continue without it
        calculate(query, 0, query.length, forComparison);
        return;
      }

      //console.log(`analyticsQuery`, analyticsQueries)

      // If none of analytics query failed, sum them up and merge with reports stats
      let errors = [];
      analyticsQueries.forEach((query, i) => {
        const domain = domainLabelForAnalyticsProperty(analyticsProperties[i]);
        if (query.total?.error) {
          errors.push({domain, message: query.total.error});
        }
        if (query.daily?.error) {
          errors.push({domain, message: query.daily.error});
        }
      });

      if (!errors.length) {
        // Merge analytics query data
        let mergedAnalyticsStats = {};

        analyticsQueries.map(query => {
          query.daily.map(day => {
            if (!mergedAnalyticsStats[day.date]) {
              mergedAnalyticsStats[day.date] = {
                pageviews: parseInt(day.pageviews),
                visits: parseInt(day.visits),
                users: parseInt(day.users),
                pageviewsPerSession: parseFloat(day.pageviewsPerSession),
                sessionsPerUser: parseFloat(day.sessionsPerUser),
              };
            } else {
              mergedAnalyticsStats[day.date] = {
                pageviews: mergedAnalyticsStats[day.date].pageviews + parseInt(day.pageviews),
                visits: mergedAnalyticsStats[day.date].visits + parseInt(day.visits),
                users: mergedAnalyticsStats[day.date].users + parseInt(day.users),
                pageviewsPerSession: parseFloat(day.pageviewsPerSession),
                sessionsPerUser: parseFloat(day.sessionsPerUser),
              };
            }
          });
        });

        // Merge analytics totals
        let mergedAnalyticsTotals = {
          users: 0,
          sessionsPerUser: 0,
          visits: 0,
          pageviewsPerSession: 0,
        };

        analyticsQueries.map(query => {
          mergedAnalyticsTotals = {
            users: mergedAnalyticsTotals.users + parseInt(query.total.users),
            sessionsPerUser:
              mergedAnalyticsTotals.sessionsPerUser + parseFloat(query.total.sessionsPerUser),
            visits: mergedAnalyticsTotals.visits + parseInt(query.total.visits),
            pageviewsPerSession:
              mergedAnalyticsTotals.pageviewsPerSession +
              parseFloat(query.total.pageviewsPerSession),
          };
        });

        mergedAnalyticsTotals = {
          ...mergedAnalyticsTotals,
          sessionsPerUser: mergedAnalyticsTotals.sessionsPerUser / analyticsQueries.length,
          pageviewsPerSession: mergedAnalyticsTotals.pageviewsPerSession / analyticsQueries.length,
        };

        // Merge merged analytics data with reports stats
        query.map((row, index) => {
          query[index] = {...query[index], ...mergedAnalyticsStats[query[index].date]};
        });

        // Add calculations
        query.map((day, i) => {
          // Add eur per 1000 PW
          query[i].pvRPM = (day['Rev'] / (day['pageviews'] / 1000)).toFixed(2);
          query[i].sRPM = (day['Rev'] / (day['visits'] / 1000)).toFixed(2);
          query[i].uRPM = (day['Rev'] / (day['users'] / 1000)).toFixed(2);
        });

        if (forComparison) {
          setAnalyticsComparisonLoaded(true);
        }

        if (!forComparison) {
          setAnalyticsLoaded(true);
        }

        calculate(query, 0, query.length, forComparison, mergedAnalyticsTotals);
      } else {
        calculate(query, 0, query.length, forComparison);
        const seen = new Set();
        const unique = [];
        for (const e of errors) {
          const k = `${e.domain}\0${e.message}`;
          if (!seen.has(k)) {
            seen.add(k);
            unique.push(e);
          }
        }
        setAnalyticsErrors(unique);
        //console.log('analyticsQueries', analyticsQueries);
      }
    } else {
      // If analytics is not needed trigger calculate function anyways

      calculate(query, 0, query.length, forComparison);
    }
  };

  let calculate = (
    data,
    indexFrom,
    indexTo,
    forComparison = false,
    mergedAnalyticsTotals = false
  ) => {
    // Prevent crash when changing periods rapidly
    if (!data.length) {
      return;
    }

    data = data.slice(indexFrom, indexTo + 1);

    let calculations = {
      dateFrom: data[0].date,
      dateTo: data[data.length - 1].date,
      rev: data.reduce((total, day) => total + parseFloat(day.Rev), 0).toFixed(2),
      imp: data.reduce((total, day) => total + parseFloat(day['All Imp']), 0).toFixed(2),
      paidImp: data.reduce((total, day) => total + parseFloat(day['Paid Imp']), 0).toFixed(2),
      allImpRefresh: data.reduce((total, day) => total + parseFloat(day['All Imp Refresh']), 0),
      refreshRev: data
        .reduce(
          (total, day) => total + (day['Rev Refresh'] ? parseFloat(day['Rev Refresh']) : 0),
          0
        )
        .toFixed(2),
      viewability: (
        data
          .filter(day => parseFloat(day.Viewability) !== 0)
          .reduce(
            (total, day) => total + parseFloat(day['Paid Imp'] * parseFloat(day['Viewability'])),
            0
          ) /
        data
          .filter(day => parseFloat(day.Viewability) !== 0)
          .reduce((total, day) => total + parseFloat(day['Paid Imp']), 0)
      ).toFixed(2),
      CTR: (
        data
          .filter(day => parseFloat(day.CTR) !== 0)
          .reduce((total, day) => total + parseFloat(day['Paid Imp'] * parseFloat(day['CTR'])), 0) /
        data
          .filter(day => parseFloat(day.CTR) !== 0)
          .reduce((total, day) => total + parseFloat(day['Paid Imp']), 0)
      ).toFixed(2),
      refreshRate:
        props.login.refresh_enabled !== 1
          ? null
          : (data.reduce((total, day) => total + parseFloat(day['All Imp Refresh']), 0) * 100) /
            data.reduce((total, day) => total + parseFloat(day['All Imp']), 0),

      // Analytics calculation
      pageviews: data.reduce(
        (total, day) => total + (isNaN(day.pageviews) ? 0 : parseFloat(day.pageviews)),
        0
      ),
      users: mergedAnalyticsTotals
        ? mergedAnalyticsTotals.users
        : data.reduce((total, day) => total + (isNaN(day.users) ? 0 : parseFloat(day.users)), 0),
      visits: mergedAnalyticsTotals
        ? mergedAnalyticsTotals.visits
        : data.reduce((total, day) => total + (isNaN(day.visits) ? 0 : parseFloat(day.visits)), 0),
      pageviewsPerSession: mergedAnalyticsTotals
        ? mergedAnalyticsTotals.pageviewsPerSession
        : data.reduce(
            (total, day) =>
              total + (isNaN(day.pageviewsPerSession) ? 0 : parseFloat(day.pageviewsPerSession)),
            0
          ) / data.length,
      sessionsPerUser: mergedAnalyticsTotals
        ? mergedAnalyticsTotals.sessionsPerUser
        : data.reduce(
            (total, day) =>
              total + (isNaN(day.sessionsPerUser) ? 0 : parseFloat(day.sessionsPerUser)),
            0
          ) / data.length,
    };

    if (forComparison) {
      setCalculatedComparison(calculations);
    } else {
      setState('calculated', calculations);
    }
  };

  const [handleZoom] = useDebouncedCallback(event => {
    // console.log('graph props handleZoom', event)
    // calculate(publisherStats, event.startIndex, event.endIndex, false);
  }, 0);

  const toggleMetric = metric => {
    if (!activeMetrics.includes(metric)) {
      setActiveMetrics(currentMetrics => [...currentMetrics, metric]);
      ReactGA.event({
        category: 'Individual metric enable',
        action: 'click',
        label: metric,
      });
    } else {
      ReactGA.event({
        category: 'Individual metric disable',
        action: 'click',
        label: metric,
      });
      setActiveMetrics(currentMetrics =>
        currentMetrics.filter(currentMetric => currentMetric !== metric)
      );
    }

    chartRef.current.state.chart.graphs.map(graph => {
      if (graph.valueField === metric) {
        if (graph.hidden) {
          chartRef.current.state.chart.showGraph(graph);
        } else {
          chartRef.current.state.chart.hideGraph(graph);
        }
      }
    });
  };

  const handleGraphTypeChange = type => {
    let graphType = type;

    if (type.target) {
      graphType = type.target.value;
    }

    let metricsGroupName;
    if (type === 4) {
      metricsGroupName = 'All metrics';
    }
    if (type === 3) {
      metricsGroupName = 'Audience';
    }
    if (type === 2) {
      metricsGroupName = 'Performance';
    }
    if (type === 1) {
      metricsGroupName = 'Monetization';
    }

    ReactGA.event({
      category: 'metrics category change',
      action: 'click',
      label: metricsGroupName,
    });

    setGraphType(graphType);

    if (selection.value.placement) {
      let metrics = metricGroups[graphType].filter(
        activeMetric => !metricGroups[3].includes(activeMetric)
      );
      setActiveMetrics(metrics);
    } else {
      setActiveMetrics(metricGroups[graphType]);
    }

    if (!isMobile) {
      chartRef.current.state.chart.graphs.map(graph => {
        if (metricGroups[graphType].includes(graph.valueField) || graphType === 0) {
          //console.clear()
          //                    console.log(chartRef.current.state.chart)
          if (
            graph.hidden &&
            selection.value.placement &&
            !metricGroups[3].includes(graph.valueField)
          ) {
            chartRef.current.state.chart.showGraph(graph);
          }
        } else {
          if (!graph.hidden) {
            chartRef.current.state.chart.hideGraph(graph);
          }
        }
      });
    }
  };

  return (
    <Container
      maxWidth="xl"
      style={{
        textAlign: 'left',
      }}
    >
      {isMobile && (
        <Grid container>
          <Grid item xs={12}>
            {props.login.menu && (
              <AccountsPropertiesSelection
                isMobile={isMobile}
                comparisonPeriod={comparisonPeriod}
              />
            )}
          </Grid>
        </Grid>
      )}

      <FadeIn>
        <Grid container spacing={1} className={classes.containerWrapper}>
          {props.login.realtime === 1 && isMobile && (
            <Grid item xs={12}>
              <Realtime isMobile={isMobile} />
            </Grid>
          )}

          <Grid item xs={12}>
            {!isMobile ? (
              <Box display="flex" flexWrap="wrap" alignItems="center" width="100%" style={{gap: 0}}>
                <Box
                  display="flex"
                  flex="1 1 auto"
                  minWidth={0}
                  alignItems="center"
                  flexWrap="wrap"
                  style={{gap: 0}}
                >
                  {props.login.daily_setupad && (
                    <DataTypeSelector inDropdown={false} marginLeft={8} />
                  )}
                  <PeriodPicker isMobile={isMobile} />
                  <Box display="flex" alignItems="center" style={{marginLeft: 10}}>
                    <Switch
                      disabled={!calculated}
                      color="primary"
                      checked={comparisonEnabled}
                      onChange={e => {
                        setPersistentState('comparisonEnabled', e.target.checked);

                        ReactGA.event({
                          category: 'comparison switch',
                          action: 'click',
                          label: e.target.checked ? 'enable' : 'disable',
                        });
                      }}
                    />
                    <ComparisonModeSelect compact disabled={!calculated} />
                  </Box>
                </Box>
                <Box flex="0 0 auto" ml="auto">
                  <TimezonePicker disabled={true} />
                </Box>
              </Box>
            ) : (
              <PeriodPicker isMobile={isMobile} />
            )}

            {calculated && isMobile && activeMetrics && props.login && (
              <MetricsDisplay
                period={period}
                comparisonPeriod={comparisonPeriod}
                publisherStats={publisherStats}
                publisherStatsComparison={publisherStatsComparison}
                graphType={graphType}
                login={props.login}
                calculated={calculated}
                calculatedComparison={calculatedComparison}
                activeMetrics={activeMetrics}
                selection={selection}
                metricGroups={metricGroups}
                analyticsLoaded={analyticsLoaded}
                assignedGoogleProperties={props.assignedGoogleProperties}
                performanceEnabled={
                  props.login.refresh_enabled === 1 ||
                  props.login.refresh_enabled === 1 ||
                  props.login.viewability_enabled === 1 ||
                  props.login.rCPM_enabled === 1 ||
                  props.login.ctr_enabled === 1
                }
                handleGraphTypeChange={handleGraphTypeChange}
              />
            )}

            {calculated && !isMobile && (
              <>
                <Grid container alignItems={'center'} className={classes.statsWrapper}>
                  <Grid item xs={2}>
                    <Button
                      onClick={() => handleGraphTypeChange(1)}
                      size={'small'}
                      variant={
                        JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[1])
                          ? 'contained'
                          : 'outlined'
                      }
                      color={
                        JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[1])
                          ? 'primary'
                          : 'default'
                      }
                      className={classes.button}
                    >
                      Monetization
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{position: 'relative'}}
                    className={!activeMetrics.includes('Rev') ? classes.lowerOpacity : ''}
                    onClick={() => toggleMetric('Rev')}
                  >
                    <div className={classes.metricWrapper}>
                      <Tooltip
                        arrow
                        title={`Total ${publishersHasOwnGam ? 'gross' : 'net'} revenue.`}
                        placement="bottom"
                      >
                        <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                          Revenue
                          <Info
                            style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                            fontSize={'small'}
                          />
                        </Typography>
                      </Tooltip>

                      <Typography variant="h5">
                        {calculated.rev}€{/*{'' + millify(calculated.rev) + '€'}*/}
                        {calculatedComparison && (
                          <Trend
                            period={period}
                            comparisonPeriod={comparisonPeriod}
                            current={calculated.rev}
                            previous={calculatedComparison.rev}
                          />
                        )}
                      </Typography>
                    </div>
                    {activeMetrics.includes('Rev') && <div className={classes.revIndicator} />}
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{position: 'relative'}}
                    className={!activeMetrics.includes('All Imp') ? classes.lowerOpacity : ''}
                    onClick={() => toggleMetric('All Imp')}
                  >
                    <div className={classes.metricWrapper}>
                      <Tooltip
                        arrow
                        title={'Number of ad server requests. Requests to start the auction.'}
                      >
                        <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                          Requests{' '}
                          <Info
                            style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                            fontSize={'small'}
                          />
                        </Typography>
                      </Tooltip>

                      <Typography variant="h5">
                        {'' + millify(calculated.imp)}
                        {calculatedComparison && (
                          <Trend
                            period={period}
                            comparisonPeriod={comparisonPeriod}
                            current={calculated.imp}
                            previous={calculatedComparison.imp}
                          />
                        )}
                      </Typography>
                    </div>
                    {activeMetrics.includes('All Imp') && (
                      <div className={classes.AllImpIndicitor} />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{position: 'relative'}}
                    className={!activeMetrics.includes('Paid Imp') ? classes.lowerOpacity : ''}
                    onClick={() => toggleMetric('Paid Imp')}
                  >
                    <div className={classes.metricWrapper}>
                      <Tooltip
                        arrow
                        title={
                          'The number of paid ad impressions delivered as a result of successful auctions.'
                        }
                      >
                        <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                          Impressions{' '}
                          <Info
                            style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                            fontSize={'small'}
                          />
                        </Typography>
                      </Tooltip>

                      <Typography variant="h5">
                        {millify(calculated.paidImp)}
                        {calculatedComparison && (
                          <Trend
                            period={period}
                            comparisonPeriod={comparisonPeriod}
                            current={calculated.paidImp}
                            previous={calculatedComparison.paidImp}
                          />
                        )}
                      </Typography>
                    </div>
                    {activeMetrics.includes('Paid Imp') && (
                      <div className={classes.PaidImpIndicitor} />
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{position: 'relative'}}
                    className={!activeMetrics.includes('eCPM') ? classes.lowerOpacity : ''}
                    onClick={() => toggleMetric('eCPM')}
                  >
                    <div className={classes.metricWrapper}>
                      <Tooltip
                        arrow
                        interactive
                        title={
                          <React.Fragment>
                            Effective cost per thousand impressions. The average{' '}
                            {publishersHasOwnGam ? 'gross' : ''} revenue earned per 1000 ad
                            impressions.
                            <br />
                            <Link
                              href="https://setupad.com/blog/cpm-vs-ecpm-everything-publishers-should-know/"
                              target={'_blank'}
                              color="primary"
                            >
                              Read more
                            </Link>
                          </React.Fragment>
                        }
                      >
                        <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                          eCPM{' '}
                          <Info
                            style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                            fontSize={'small'}
                          />
                        </Typography>
                      </Tooltip>

                      <Typography variant="h5">
                        {calculated.rev != 0 && calculated.paidImp != 0
                          ? ((calculated.rev / calculated.paidImp) * 1000).toFixed(2)
                          : 0}
                        €
                        {calculatedComparison && (
                          <Trend
                            period={period}
                            comparisonPeriod={comparisonPeriod}
                            current={calculated.rev / calculated.paidImp}
                            previous={calculatedComparison.rev / calculatedComparison.paidImp}
                          />
                        )}
                      </Typography>
                    </div>

                    {activeMetrics.includes('eCPM') && <div className={classes.eCPMIndicator} />}
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    style={{position: 'relative'}}
                    className={!activeMetrics.includes('fillRate') ? classes.lowerOpacity : ''}
                    onClick={() => toggleMetric('fillRate')}
                  >
                    <div className={classes.metricWrapper}>
                      <Tooltip
                        arrow
                        interactive
                        title={
                          <React.Fragment>
                            The percentage of ad requests that resulted in paid ad impressions.
                            Calculated as (Impressions / Requests) * 100.
                            <br />
                            <Link
                              href="https://setupad.com/blog/ecpm-rcpm-and-fill-rate-everything-publishers-should-know/"
                              target={'_blank'}
                              color="primary"
                            >
                              Read more
                            </Link>
                          </React.Fragment>
                        }
                      >
                        <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                          Fill rate{' '}
                          <Info
                            style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                            fontSize={'small'}
                          />
                        </Typography>
                      </Tooltip>

                      <Typography variant="h5" component="h2">
                        {calculated.paidImp != 0 && calculated.imp != 0
                          ? ((calculated.paidImp * 100) / calculated.imp).toFixed(2)
                          : 0}
                        %
                        {calculatedComparison && calculated.paidImp != 0 && calculated.imp != 0 && (
                          <Trend
                            period={period}
                            comparisonPeriod={comparisonPeriod}
                            current={((calculated.paidImp * 100) / calculated.imp).toFixed(2)}
                            previous={(
                              (calculatedComparison.paidImp * 100) /
                              calculatedComparison.imp
                            ).toFixed(2)}
                          />
                        )}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>

                {(props.login.refresh_enabled === 1 ||
                  props.login.refresh_enabled === 1 ||
                  props.login.viewability_enabled === 1 ||
                  props.login.rCPM_enabled === 1 ||
                  props.login.ctr_enabled === 1) && (
                  <Grid container alignItems={'center'} className={classes.statsWrapper}>
                    <Grid item xs={2}>
                      <Button
                        onClick={() => handleGraphTypeChange(2)}
                        size={'small'}
                        variant={
                          JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[2])
                            ? 'contained'
                            : 'outlined'
                        }
                        color={
                          JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[2])
                            ? 'primary'
                            : 'default'
                        }
                        className={classes.button}
                      >
                        Performance
                      </Button>
                    </Grid>

                    {props.login.refresh_enabled === 1 && (
                      <Grid
                        item
                        xs={2}
                        style={{position: 'relative'}}
                        className={
                          !activeMetrics.includes('Rev Refresh') ? classes.lowerOpacity : ''
                        }
                        onClick={() => toggleMetric('Rev Refresh')}
                      >
                        <div className={classes.metricWrapper}>
                          <Tooltip
                            arrow
                            interactive
                            title={
                              <React.Fragment>
                                {publishersHasOwnGam ? 'Gross revenue' : 'Revenue'} from Viewable
                                Bid Optimization, an algorithm that refreshes ads only when
                                beneficial for publishers to maintain a high average eCPM.
                                <br />
                                <Link
                                  href="https://setupad.com/blog/ad-refresh-viewable-bid-optimization-begginers-guide/"
                                  target={'_blank'}
                                  color="primary"
                                >
                                  Read more
                                </Link>
                              </React.Fragment>
                            }
                          >
                            <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                              Refresh Revenue
                              <Info
                                style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                                fontSize={'small'}
                              />
                            </Typography>
                          </Tooltip>

                          <Typography variant="h5" component="h1">
                            {millify(calculated.refreshRev)}€
                            {calculatedComparison && (
                              <Trend
                                period={period}
                                comparisonPeriod={comparisonPeriod}
                                current={calculated.refreshRev}
                                previous={calculatedComparison.refreshRev}
                              />
                            )}
                          </Typography>
                        </div>
                        {activeMetrics.includes('Rev Refresh') && (
                          <div className={classes.revRefreshIndicator} />
                        )}
                      </Grid>
                    )}

                    {props.login.refresh_enabled === 1 && (
                      <Grid
                        item
                        xs={2}
                        style={{position: 'relative'}}
                        className={
                          !activeMetrics.includes('refreshRate') ? classes.lowerOpacity : ''
                        }
                        onClick={() => toggleMetric('refreshRate')}
                      >
                        <div className={classes.metricWrapper}>
                          <Tooltip
                            arrow
                            interactive
                            title={
                              <React.Fragment>
                                The rate at which ads are refreshed by the Viewable Bid Optimization
                                algorithm, ensuring refreshes occur only when beneficial to maintain
                                a high average eCPM.
                                <br />
                                <Link
                                  href="https://setupad.com/blog/ad-refresh-viewable-bid-optimization-begginers-guide/"
                                  target={'_blank'}
                                  color="primary"
                                >
                                  Read more
                                </Link>
                              </React.Fragment>
                            }
                          >
                            <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                              Smart Refresh Rate{' '}
                              <Info
                                style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                                fontSize={'small'}
                              />
                            </Typography>
                          </Tooltip>

                          <Typography variant="h5" component="h1">
                            {!isFinite(calculated.refreshRate) || isNaN(calculated.refreshRate)
                              ? 'N/A'
                              : `${calculated.refreshRate.toFixed(2)}%`}

                            {calculatedComparison &&
                              isFinite(
                                calculated.refreshRate && !isNaN(calculated.refreshRate)
                              ) && (
                                <Trend
                                  period={period}
                                  comparisonPeriod={comparisonPeriod}
                                  current={calculated.refreshRate.toFixed(2)}
                                  previous={calculatedComparison.refreshRate.toFixed(2)}
                                />
                              )}
                          </Typography>
                        </div>
                        {activeMetrics.includes('refreshRate') && (
                          <div className={classes.refreshRateIndicator} />
                        )}
                      </Grid>
                    )}

                    {props.login.viewability_enabled === 1 && (
                      <Grid
                        item
                        xs={2}
                        style={{position: 'relative'}}
                        className={
                          !activeMetrics.includes('Viewability') ? classes.lowerOpacity : ''
                        }
                        onClick={() => toggleMetric('Viewability')}
                      >
                        <div className={classes.metricWrapper}>
                          <Tooltip
                            arrow
                            interactive
                            title={
                              <React.Fragment>
                                The percentage of ads that were visible in the browser for at least
                                1 continuous second and displayed at least 50% of the ad's pixels.
                                Measured by Google across Google Ad Exchange impressions.
                                <br />
                                <Link
                                  href="https://setupad.com/blog/why-ad-viewability-important-digital-advertising/"
                                  target={'_blank'}
                                  color="primary"
                                >
                                  Read more
                                </Link>
                              </React.Fragment>
                            }
                          >
                            <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                              Viewability{' '}
                              <Info
                                style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                                fontSize={'small'}
                              />
                            </Typography>
                          </Tooltip>

                          <Typography variant="h5" component="h2">
                            {isNaN(calculated.viewability) ? 'N/A' : `${calculated.viewability}%`}

                            {calculatedComparison && !isNaN(calculated.viewability) && (
                              <Trend
                                period={period}
                                comparisonPeriod={comparisonPeriod}
                                current={calculated.viewability}
                                previous={calculatedComparison.viewability}
                              />
                            )}
                          </Typography>
                        </div>
                        {activeMetrics.includes('Viewability') && (
                          <div className={classes.viewabilityIndicitor} />
                        )}
                      </Grid>
                    )}

                    {props.login.rCPM_enabled === 1 && (
                      <Grid
                        item
                        xs={2}
                        style={{position: 'relative'}}
                        className={!activeMetrics.includes('rCPM') ? classes.lowerOpacity : ''}
                        onClick={() => toggleMetric('rCPM')}
                      >
                        <div className={classes.metricWrapper}>
                          <Tooltip
                            arrow
                            interactive
                            title={
                              <React.Fragment>
                                "Request CPM" or "Revenue CPM." The average{' '}
                                {publishersHasOwnGam ? 'gross' : ''} revenue per 1000 ad requests,
                                including those not filled. The difference between rCPM and eCPM is
                                equivalent to fill rate.
                                <br />
                                <Link
                                  href="https://setupad.com/blog/ecpm-rcpm-and-fill-rate-everything-publishers-should-know/"
                                  target={'_blank'}
                                  color="primary"
                                >
                                  Read more
                                </Link>
                              </React.Fragment>
                            }
                          >
                            <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                              rCPM{' '}
                              <Info
                                style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                                fontSize={'small'}
                              />
                            </Typography>
                          </Tooltip>

                          <Typography
                            variant="h5"
                            component="h2"
                            //style={{color: "#e75d3c"}}
                          >
                            {calculated.rev != 0 && calculated.imp != 0
                              ? ((calculated.rev / calculated.imp) * 1000).toFixed(2)
                              : 0}
                            €
                            {calculatedComparison && (
                              <Trend
                                period={period}
                                comparisonPeriod={comparisonPeriod}
                                current={calculated.rev / calculated.imp}
                                previous={calculatedComparison.rev / calculatedComparison.imp}
                              />
                            )}
                          </Typography>
                        </div>
                        {activeMetrics.includes('rCPM') && (
                          <div className={classes.RCPMIndicator} />
                        )}
                      </Grid>
                    )}

                    {props.login.ctr_enabled === 1 && (
                      <Grid
                        item
                        xs={2}
                        style={{position: 'relative'}}
                        className={!activeMetrics.includes('CTR') ? classes.lowerOpacity : ''}
                        onClick={() => toggleMetric('CTR')}
                      >
                        <div className={classes.metricWrapper}>
                          <Tooltip
                            arrow
                            title={
                              'The percentage of ad impressions that resulted in a user click. Measured by Google across Google Ad Exchange impressions.'
                            }
                          >
                            <Typography variant={'caption'} color={'textSecondary'} gutterBottom>
                              CTR{' '}
                              <Info
                                style={{opacity: 0.5, left: 5, top: 4, position: 'relative'}}
                                fontSize={'small'}
                              />
                            </Typography>
                          </Tooltip>
                          <Typography variant="h5" component="h2">
                            {isNaN(calculated.CTR) ? 'N/A' : `${calculated.CTR}%`}

                            {calculatedComparison && !isNaN(calculated.CTR) && (
                              <Trend
                                period={period}
                                comparisonPeriod={comparisonPeriod}
                                current={calculated.CTR}
                                previous={calculatedComparison.CTR}
                              />
                            )}
                          </Typography>
                        </div>
                        {activeMetrics.includes('CTR') && <div className={classes.CTRIndicator} />}
                      </Grid>
                    )}
                  </Grid>
                )}

                {props.assignedGoogleProperties !== 'error' &&
                  (props.assignedGoogleProperties.find(
                    property => property.domainId == selection.value.domain
                  ) ||
                    (Object.keys(props.assignedGoogleProperties).length > 0 &&
                      !selection.value.publisher)) &&
                  analyticsErrors.length === 0 && (
                    <>
                      {!analyticsLoaded && (
                        <Grid container alignItems={'center'} className={classes.statsWrapper}>
                          <Grid item xs={2}>
                            <Button
                              size={'small'}
                              variant={
                                JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[3])
                                  ? 'contained'
                                  : 'outlined'
                              }
                              color={
                                JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[3])
                                  ? 'primary'
                                  : 'default'
                              }
                              className={classes.button}
                            >
                              Audience
                              <img
                                src={AnalyticsIcon}
                                style={{position: 'relative', top: 0, right: -3, height: 13}}
                              />
                            </Button>
                          </Grid>
                          <Grid item xs={2}>
                            <Skeleton width={'90%'} height={80} />
                          </Grid>
                          <Grid item xs={2}>
                            <Skeleton width={'90%'} height={80} />
                          </Grid>
                          <Grid item xs={2}>
                            <Skeleton width={'90%'} height={80} />
                          </Grid>
                          <Grid item xs={2}>
                            <Skeleton width={'90%'} height={80} />
                          </Grid>
                          <Grid item xs={2}>
                            <Skeleton width={'90%'} height={80} />
                          </Grid>
                        </Grid>
                      )}

                      {analyticsLoaded && (
                        <Grid container alignItems={'center'} className={classes.statsWrapper}>
                          <Grid item xs={2}>
                            <Button
                              onClick={() =>
                                selection.value.placement ? '' : handleGraphTypeChange(3)
                              }
                              size={'small'}
                              variant={
                                JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[3])
                                  ? 'contained'
                                  : 'outlined'
                              }
                              color={
                                JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[3])
                                  ? 'primary'
                                  : 'default'
                              }
                              className={classes.button}
                            >
                              Audience
                              <img
                                src={AnalyticsIcon}
                                style={{position: 'relative', top: 0, right: -3, height: 13}}
                              />
                            </Button>
                          </Grid>

                          {selection.value.placement && (
                            <Grid item xs={10}>
                              <Typography align={'center'} variant={'body'} color={'textSecondary'}>
                                Available only on a Domain and Account Total levels
                              </Typography>
                            </Grid>
                          )}
                          {!selection.value.placement && (
                            <>
                              <Grid
                                item
                                xs={2}
                                style={{position: 'relative'}}
                                className={
                                  !activeMetrics.includes('uRPM') ||
                                  !props.assignedGoogleProperties.find(
                                    property => property.domainId == selection.value.domain
                                  )
                                    ? classes.lowerOpacity
                                    : ''
                                }
                                onClick={() => toggleMetric('uRPM')}
                              >
                                <div className={classes.metricWrapperAudience}>
                                  <Tooltip
                                    arrow
                                    title={
                                      "Revenue per 1000 users. The number of users is obtained from Google Analytics data. uRPM is available on domain level only since it's non-cumulative statistics."
                                    }
                                    placement="bottom"
                                  >
                                    <Typography
                                      variant={'caption'}
                                      color={'textSecondary'}
                                      gutterBottom
                                    >
                                      {/*<img src={AnalyticsIcon} style={{paddingRight: 10}}/>*/}
                                      uRPM{' '}
                                      <Info
                                        style={{
                                          opacity: 0.5,
                                          left: 5,
                                          top: 4,
                                          position: 'relative',
                                        }}
                                        fontSize={'small'}
                                      />
                                    </Typography>
                                  </Tooltip>

                                  <Typography variant="h5">
                                    {props.assignedGoogleProperties.find(
                                      property => property.domainId == selection.value.domain
                                    )
                                      ? `${(calculated.rev / (calculated.users / 1000)).toFixed(
                                          2
                                        )}€`
                                      : `ND`}

                                    {calculatedComparison &&
                                      analyticsComparisonLoaded &&
                                      props.assignedGoogleProperties.find(
                                        property => property.domainId == selection.value.domain
                                      ) && (
                                        <Trend
                                          period={period}
                                          comparisonPeriod={comparisonPeriod}
                                          current={calculated.rev / (calculated.users / 1000)}
                                          previous={
                                            calculatedComparison.rev /
                                            (calculatedComparison.users / 1000)
                                          }
                                        />
                                      )}

                                    <Typography
                                      variant={'caption'}
                                      style={{lineHeight: '0.5rem'}}
                                      color={'textPrimary'}
                                      display={'block'}
                                    >
                                      {props.assignedGoogleProperties.find(
                                        property => property.domainId == selection.value.domain
                                      )
                                        ? `(${millify(calculated.users)} Users)`
                                        : `(non-cumulative data)`}
                                    </Typography>
                                  </Typography>
                                </div>

                                {activeMetrics.includes('uRPM') &&
                                  props.assignedGoogleProperties.find(
                                    property => property.domainId == selection.value.domain
                                  ) && <div className={classes.uRPMIndicator} />}
                              </Grid>

                              <Grid
                                item
                                xs={2}
                                style={{position: 'relative'}}
                                className={
                                  !activeMetrics.includes('sRPM') ? classes.lowerOpacity : ''
                                }
                                onClick={() => toggleMetric('sRPM')}
                              >
                                <div className={classes.metricWrapperAudience}>
                                  <Tooltip
                                    arrow
                                    title={`Revenue per 1000 sessions. The number of sessions is obtained from connected Google Analytics data. sRPM is available on domain and account levels.`}
                                    placement="bottom"
                                  >
                                    <Typography
                                      variant={'caption'}
                                      color={'textSecondary'}
                                      gutterBottom
                                    >
                                      {/*<img src={AnalyticsIcon} style={{paddingRight: 10}}/>*/}
                                      sRPM{' '}
                                      <Info
                                        style={{
                                          opacity: 0.5,
                                          left: 5,
                                          top: 4,
                                          position: 'relative',
                                        }}
                                        fontSize={'small'}
                                      />
                                    </Typography>
                                  </Tooltip>

                                  <Typography variant="h5">
                                    {(calculated.rev / (calculated.visits / 1000)).toFixed(2)}€
                                    {calculatedComparison && analyticsComparisonLoaded && (
                                      <Trend
                                        period={period}
                                        comparisonPeriod={comparisonPeriod}
                                        current={calculated.rev / (calculated.visits / 1000)}
                                        previous={
                                          calculatedComparison.rev /
                                          (calculatedComparison.visits / 1000)
                                        }
                                      />
                                    )}
                                    <Typography
                                      variant={'caption'}
                                      style={{lineHeight: '0.5rem'}}
                                      color={'textPrimary'}
                                      display={'block'}
                                    >
                                      ({millify(calculated.visits)} Sessions)
                                    </Typography>
                                  </Typography>
                                </div>
                                {activeMetrics.includes('sRPM') && (
                                  <div className={classes.sRPMIndicator} />
                                )}
                              </Grid>

                              <Grid
                                item
                                xs={2}
                                style={{position: 'relative'}}
                                className={
                                  !activeMetrics.includes('pvRPM') ? classes.lowerOpacity : ''
                                }
                                onClick={() => toggleMetric('pvRPM')}
                              >
                                <div className={classes.metricWrapperAudience}>
                                  <Tooltip
                                    arrow
                                    title={`Revenue per 1000 pageviews. The number of pageviews is obtained from connected Google Analytics data. pvRPM is available on domain and account levels.`}
                                    placement="bottom"
                                  >
                                    <Typography
                                      variant={'caption'}
                                      color={'textSecondary'}
                                      gutterBottom
                                    >
                                      pvRPM{' '}
                                      <Info
                                        style={{
                                          opacity: 0.5,
                                          left: 5,
                                          top: 4,
                                          position: 'relative',
                                        }}
                                        fontSize={'small'}
                                      />
                                    </Typography>
                                  </Tooltip>

                                  <Typography variant="h5">
                                    {(calculated.rev / (calculated.pageviews / 1000)).toFixed(2)}€
                                    {calculatedComparison && analyticsComparisonLoaded && (
                                      <Trend
                                        period={period}
                                        comparisonPeriod={comparisonPeriod}
                                        current={calculated.rev / (calculated.pageviews / 1000)}
                                        previous={
                                          calculatedComparison.rev /
                                          (calculatedComparison.pageviews / 1000)
                                        }
                                      />
                                    )}
                                    <Typography
                                      variant={'caption'}
                                      style={{lineHeight: '0.5rem'}}
                                      color={'textPrimary'}
                                      display={'block'}
                                    >
                                      ({millify(calculated.pageviews)} Pageviews)
                                    </Typography>
                                  </Typography>
                                </div>
                                {activeMetrics.includes('pvRPM') && (
                                  <div className={classes.pvRPMIndicator} />
                                )}

                                {/*<Typography variant={"caption"} color={"textSecondary"}>*/}
                                {/*{props.assignedGoogleProperties.find(property => property.domainId == selection.value.domain).property.label}*/}
                                {/*</Typography>*/}
                              </Grid>

                              <Grid
                                item
                                xs={2}
                                style={{position: 'relative'}}
                                className={
                                  !activeMetrics.includes('sessionsPerUser') ||
                                  !props.assignedGoogleProperties.find(
                                    property => property.domainId == selection.value.domain
                                  )
                                    ? classes.lowerOpacity
                                    : ''
                                }
                              >
                                <div className={classes.metricWrapperAudience}>
                                  <Tooltip
                                    arrow
                                    title={`Number of sessions per user. Average number of sessions made by users. Obtained from connected Google Analytics data. Available on domain level only since it's non-cumulative statistics. `}
                                    placement="bottom"
                                  >
                                    <Typography
                                      variant={'caption'}
                                      color={'textSecondary'}
                                      gutterBottom
                                    >
                                      Sessions / User{' '}
                                      <Info
                                        style={{
                                          opacity: 0.5,
                                          left: 5,
                                          top: 4,
                                          position: 'relative',
                                        }}
                                        fontSize={'small'}
                                      />
                                    </Typography>
                                  </Tooltip>

                                  <Typography variant="h5">
                                    {props.assignedGoogleProperties.find(
                                      property => property.domainId == selection.value.domain
                                    )
                                      ? millify(calculated.sessionsPerUser)
                                      : `ND`}

                                    {calculatedComparison &&
                                      props.assignedGoogleProperties.find(
                                        property => property.domainId == selection.value.domain
                                      ) && (
                                        <Trend
                                          period={period}
                                          comparisonPeriod={comparisonPeriod}
                                          current={calculated.sessionsPerUser}
                                          previous={calculatedComparison.sessionsPerUser}
                                        />
                                      )}
                                  </Typography>

                                  <Typography
                                    variant={'caption'}
                                    style={{lineHeight: '0.5rem'}}
                                    color={'textPrimary'}
                                    display={'block'}
                                  >
                                    {props.assignedGoogleProperties.find(
                                      property => property.domainId == selection.value.domain
                                    )
                                      ? ``
                                      : `(non-cumulative data)`}
                                  </Typography>
                                </div>
                              </Grid>

                              <Grid
                                item
                                xs={2}
                                style={{position: 'relative'}}
                                className={
                                  !activeMetrics.includes('pageviewsPerSession')
                                    ? classes.lowerOpacity
                                    : ''
                                }
                              >
                                <div className={classes.metricWrapperAudience}>
                                  <Tooltip
                                    arrow
                                    title={`The number of pages per session. The average number of pages viewed during a session. Obtained from connected Google Analytics data. Available on domain and account levels.`}
                                    placement="bottom"
                                  >
                                    <Typography
                                      variant={'caption'}
                                      color={'textSecondary'}
                                      gutterBottom
                                    >
                                      {/*<img src={AnalyticsIcon} style={{paddingRight: 10}}/>*/}
                                      Pages / Session{' '}
                                      <Info
                                        style={{
                                          opacity: 0.5,
                                          left: 5,
                                          top: 4,
                                          position: 'relative',
                                        }}
                                        fontSize={'small'}
                                      />
                                    </Typography>
                                  </Tooltip>

                                  <Typography variant="h5">
                                    {calculated.pageviewsPerSession
                                      ? millify(calculated.pageviewsPerSession)
                                      : 0}

                                    {calculatedComparison && analyticsComparisonLoaded && (
                                      <Trend
                                        period={period}
                                        comparisonPeriod={comparisonPeriod}
                                        current={calculated.pageviewsPerSession}
                                        previous={calculatedComparison.pageviewsPerSession}
                                      />
                                    )}
                                  </Typography>
                                </div>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      )}
                    </>
                  )}

                {!isMobile && (
                  <Grid container direction="row-reverse">
                    <Grid item xs={12}>
                      <Button
                        size={'small'}
                        variant={
                          JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[4])
                            ? 'contained'
                            : 'outlined'
                        }
                        color={
                          JSON.stringify(activeMetrics) === JSON.stringify(metricGroups[4])
                            ? 'primary'
                            : 'default'
                        }
                        style={{marginTop: 20}}
                        className={classes.button}
                        onClick={() => handleGraphTypeChange(4)}
                      >
                        All metrics
                      </Button>

                      <DataExport
                        dataType={dataType}
                        publisherStats={publisherStats}
                        period={period}
                        calculated={calculated}
                      />

                      {props.login.username !== 'demo' && (
                        <ShareDialog
                          publisherStats={publisherStats}
                          activeMetrics={activeMetrics}
                          calculated={calculated}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {((!publisherStats && !calculated) || (publisherStats.length !== 0 && !calculated)) &&
              !isMobile &&
              analyticsErrors.length === 0 && (
                <div style={{width: '100%', textAlign: 'center'}}>
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={50}
                    animation="wave"
                    style={{marginBottom: 10, marginTop: 10}}
                  />
                  {(props.login.refresh_enabled === 1 ||
                    props.login.refresh_enabled === 1 ||
                    props.login.viewability_enabled === 1 ||
                    props.login.rCPM_enabled === 1 ||
                    props.login.ctr_enabled === 1) && (
                    <Skeleton
                      variant="rect"
                      width={'100%'}
                      height={50}
                      animation="wave"
                      style={{marginTop: 15}}
                    />
                  )}
                  {props.assignedGoogleProperties !== 'error' &&
                    (props.assignedGoogleProperties.find(
                      property => property.domainId == selection.value.domain
                    ) ||
                      Object.keys(props.assignedGoogleProperties).length > 0) &&
                    analyticsErrors.length === 0 && (
                      <Skeleton
                        variant="rect"
                        width={'100%'}
                        height={70}
                        animation="wave"
                        style={{marginTop: 15}}
                      />
                    )}
                  <Skeleton
                    variant="rect"
                    width={'20%'}
                    height={30}
                    animation="wave"
                    style={{marginTop: 25}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={420}
                    animation="wave"
                    style={{marginTop: 20}}
                  />
                </div>
              )}

            {((!publisherStats && !calculated) || (publisherStats.length !== 0 && !calculated)) &&
              isMobile && (
                <div
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    marginTop: theme.spacing(2.5),
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={33}
                    animation="wave"
                    style={{marginBottom: 16, borderRadius: 4}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={72}
                    animation="wave"
                    style={{marginBottom: 8, borderRadius: 4}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={72}
                    animation="wave"
                    style={{marginBottom: 8, borderRadius: 4}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={72}
                    animation="wave"
                    style={{marginBottom: 8, borderRadius: 4}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={72}
                    animation="wave"
                    style={{marginBottom: 8, borderRadius: 4}}
                  />
                  <Skeleton
                    variant="rect"
                    width={'100%'}
                    height={72}
                    animation="wave"
                    style={{marginBottom: 8, borderRadius: 4}}
                  />
                </div>
              )}

            {!analyticsReminderHidden &&
              props.login.google_analytics_enabled === 1 &&
              props.assignedGoogleProperties.length === 0 &&
              selection.value.domain &&
              !lscache.get('hideAnalyticsRemined') && (
                <Alert
                  severity="warning"
                  action={
                    <>
                      <Button
                        component={Link}
                        href={'/google-analytics'}
                        variant={'contained'}
                        color={'primary'}
                        size={'small'}
                        startIcon={<img src={AnalyticsIcon} style={{paddingRight: 10}} />}
                      >
                        Configuration
                      </Button>
                      <IconButton
                        size={'small'}
                        onClick={() => {
                          lscache.set('hideAnalyticsRemined', true);
                          setAnalyticsReminderHidden(true);
                        }}
                      >
                        <Close />
                      </IconButton>
                    </>
                  }
                >
                  Did you know that you can now connect your Google Analytics and view more
                  insightful metrics for the monetized domains?
                </Alert>
              )}

            {analyticsErrors.length > 0 && (
              <span style={{display: 'block', marginTop: 10, marginBottom: 10}}>
                <Alert
                  severity="warning"
                  action={
                    <Button
                      component={Link}
                      href={'/google-analytics'}
                      variant={'contained'}
                      color={'primary'}
                      size={'small'}
                      startIcon={<img src={AnalyticsIcon} style={{paddingRight: 10}} />}
                    >
                      Configuration
                    </Button>
                  }
                >
                  <Typography variant="body2" component="div" style={{fontWeight: 600}}>
                    Failed to fetch Google Analytics data
                  </Typography>
                  <Typography
                    component="div"
                    variant="body2"
                    color="textSecondary"
                    style={{marginTop: 4}}
                  >
                    Most often the linked Google account doesn&apos;t have access to the analytics
                    property, or the property still uses Universal Analytics (GA3), which is
                    deprecated.{' '}
                    {analyticsErrorDomainCount > 1 && (
                      <>
                        <strong>{analyticsErrorDomainCount}</strong> domains are affected.{' '}
                      </>
                    )}
                    <Tooltip
                      arrow
                      interactive
                      placement="bottom"
                      classes={{tooltip: classes.gaErrorTooltip}}
                      title={
                        <div className={classes.gaErrorTooltipBody}>
                          {analyticsErrors.map((e, idx) => {
                            const faviconHost = String(e.domain)
                              .replace(/ domain$/, '')
                              .trim();
                            const faviconQs = encodeURIComponent(`https://${faviconHost}`);
                            return (
                              <div
                                key={`${e.domain}-${idx}-${e.message.slice(0, 48)}`}
                                className={classes.gaErrorTooltipRow}
                              >
                                <div className={classes.gaErrorTooltipDomainRow}>
                                  <div
                                    className={classes.gaErrorTooltipDomainImage}
                                    role="presentation"
                                    style={{
                                      backgroundImage: `url(https://www.google.com/s2/favicons?domain=${faviconQs}&sz=256)`,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    component="div"
                                    className={classes.gaErrorTooltipDomain}
                                  >
                                    {e.domain}
                                  </Typography>
                                </div>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  className={classes.gaErrorTooltipMessage}
                                >
                                  {e.message}
                                </Typography>
                              </div>
                            );
                          })}
                        </div>
                      }
                    >
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        color="textSecondary"
                        underline="always"
                        aria-label="Show per-domain error messages from Google Analytics"
                        style={{
                          cursor: 'pointer',
                          verticalAlign: 'baseline',
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                        }}
                      >
                        More details.
                      </Link>
                    </Tooltip>
                  </Typography>
                </Alert>
              </span>
            )}

            {publisherStats.length === 0 && (
              <Container
                maxWidth="xl"
                style={{
                  padding: 65,
                  textAlign: 'center',
                }}
              >
                <Placeholder />
                <Typography align={'center'}>No data</Typography>
              </Container>
            )}

            <HistoricalDataAlert publisherStats={publisherStats} />

            {/*AccountsDetailsTable is rendered twice because it doesn't update publisherStats in internal state when provided, so redrawing whole graph for now*/}
            {publisherStats && publisherStats.length !== 0 && (
              <>
                {!isMobile && publisherStats && calculated && !analyticsLoaded && (
                  <AccountsDetailsTable
                    domainHistory={domainHistory}
                    activeMetrics={activeMetrics}
                    analyticsAdded={
                      (analyticsErrors.length === 0 &&
                        props.assignedGoogleProperties !== 'error' &&
                        props.assignedGoogleProperties.find(
                          property => property.domainId == selection.value.domain
                        )) ||
                      Object.keys(props.assignedGoogleProperties).length > 0
                        ? analyticsLoaded
                          ? true
                          : false
                        : false
                    }
                    analyticsProperty={props.assignedGoogleProperties.find(
                      property => property.domainId == selection.value.domain
                    )}
                    publisherStats={publisherStats}
                    chartRef={chartRef}
                    handleZoom={handleZoom}
                    login={props.login}
                  />
                )}

                {!isMobile && publisherStats && calculated && analyticsLoaded && (
                  <AccountsDetailsTable
                    domainHistory={domainHistory}
                    activeMetrics={activeMetrics}
                    analyticsAdded={
                      (analyticsErrors.length === 0 &&
                        props.assignedGoogleProperties !== 'error' &&
                        props.assignedGoogleProperties.find(
                          property => property.domainId == selection.value.domain
                        )) ||
                      Object.keys(props.assignedGoogleProperties).length > 0
                        ? analyticsLoaded
                          ? true
                          : false
                        : false
                    }
                    analyticsProperty={props.assignedGoogleProperties.find(
                      property => property.domainId == selection.value.domain
                    )}
                    publisherStats={publisherStats}
                    chartRef={chartRef}
                    handleZoom={handleZoom}
                    login={props.login}
                  />
                )}

                {!selection.label?.includes('interstitial') &&
                  calculated &&
                  props.login.ssp_table_enabled === 1 &&
                  isMobile && (
                    <DecilesView
                      totalImpressions={parseFloat(calculated.paidImp)}
                      totalRevenue={parseFloat(calculated.rev)}
                      login={props.login}
                      eCPM={((calculated.rev / calculated.paidImp) * 1000).toFixed(2)}
                      selection={selection}
                      menu={props.login.menu}
                      period={period}
                    />
                  )}
              </>
            )}
          </Grid>
        </Grid>
      </FadeIn>
    </Container>
  );
});

export default React.memo(AccountDetails);
